import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TrashIcon,
  ChevronLeftIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { statementQueries } from 'src/services/statements/queries'
import { statementApi, exportCsv } from 'src/services/statements/api'
import {
  StatementRow,
  classificationEnum,
  decisionEnum,
  statementStatusEnum,
  statementStatusLabelKeys,
  statementStatusBadgeClasses,
} from 'src/services/statements/models'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'
import { useIsJobActive, useAsyncJobStore } from 'src/stores/async-jobs'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { cn } from 'src/utils/style'
import { MatchedRowCard } from 'src/components/statements/matched-row-card'
import { UnmatchedRowCard } from 'src/components/statements/unmatched-row-card'
import { JobProgressBar } from 'src/components/statements/job-progress-bar'
import { StatementStepIndicator } from 'src/components/statements/statement-step-indicator'
import { ColumnsStep } from 'src/components/statements/columns-step'
import { AccountStep } from 'src/components/statements/account-step'
import { Modal } from 'src/components/modal'
import { ReportOverride } from 'src/components/statements/report-override'
import { resolveWizardState, wizardStepEnum, WizardStep } from 'src/services/statements/wizard'
import {
  columnsValueFromPreview,
  columnsValueToMap,
  validateColumnsValue,
  type ColumnsValue,
} from 'src/services/statements/columns'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { useOrgFeatureConfig } from 'src/hooks/use-org-feature-config'

export const StatementReview = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedStep = searchParams.get('step')

  const goToStep = useCallback(
    (step: WizardStep) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('step', step)
          return next
        },
        { replace: false },
      )
    },
    [setSearchParams],
  )

  const { data: statement, isLoading: statementLoading } = useQuery(statementQueries.retrieve(id!))
  const {
    data: rows,
    isLoading: rowsLoading,
    refetch: refetchRows,
  } = useQuery(statementQueries.rows(id!))

  const isProcessing = useIsJobActive(taskTypeEnum.STATEMENT_PROCESSING, id!)
  const isReconciling = useIsJobActive(taskTypeEnum.STATEMENT_RECONCILIATION, id!)
  const { data: featureConfig } = useOrgFeatureConfig()
  const aiReconciliationEnabled = featureConfig?.aiStatementReconciliationEnabled !== false

  const [editedFields, setEditedFields] = useState<Record<string, Record<string, unknown>>>({})
  const [selectedUnmatched, setSelectedUnmatched] = useState<Set<string>>(new Set())
  const [selectedMatched, setSelectedMatched] = useState<Set<string>>(new Set())

  // Columns step: the LLM-applied header map is fetched as a preview (raw
  // header + sample + mapping) and edited locally until the user confirms.
  const hasColumnMap = Boolean(statement?.columnMap && Object.keys(statement.columnMap).length > 0)
  const columnMapConfirmed = statement?.columnMapConfirmed === true
  const { data: columnPreview } = useQuery(
    statementQueries.columnPreview(id!, hasColumnMap && !columnMapConfirmed),
  )
  const [columnsValue, setColumnsValue] = useState<ColumnsValue>({})
  useEffect(() => {
    if (columnPreview?.columns) {
      setColumnsValue(columnsValueFromPreview(columnPreview.columns))
    }
  }, [columnPreview])
  const columnsValidation = useMemo(() => validateColumnsValue(columnsValue), [columnsValue])

  const confirmColumnsMutation = useMutation({
    mutationFn: async () => {
      return statementApi.csc.confirmColumns({
        statementId: id!,
        columnMap: columnsValueToMap(columnsValue),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
      // Drop the ?step=columns override so derivation advances past Columns.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('step')
          return next
        },
        { replace: true },
      )
    },
  })

  // Account step: preselect the confirmed account, else the detected suggestion,
  // but never replace an account the user has already picked.
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [showAccountChangeWarning, setShowAccountChangeWarning] = useState(false)
  useEffect(() => {
    if (statement) {
      setSelectedAccountId(
        (picked) => statement.paymentAccount ?? picked ?? statement.suggestedPaymentAccount ?? null,
      )
    }
  }, [statement?.paymentAccount, statement?.suggestedPaymentAccount]) // eslint-disable-line react-hooks/exhaustive-deps

  const setAccountMutation = useMutation({
    mutationFn: async (paymentAccountId: string) =>
      statementApi.csc.setAccount({ statementId: id!, paymentAccountId }),
    onSuccess: (updated) => {
      // Seed the cache so derivation advances immediately, then refresh the rest.
      queryClient.setQueryData(statementQueries.retrieve(id!).queryKey, updated)
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
      // Confirming can start (or re-run) reconciliation; show its spinner now.
      if (updated.asyncJobId) {
        restoreActive(
          buildOptimisticJob({
            id: updated.asyncJobId,
            taskType: taskTypeEnum.STATEMENT_RECONCILIATION,
            entityId: id!,
            entityType: 'Statement',
            label: t('asyncJobs.statementReconciliation'),
          }),
        )
      }
      setShowAccountChangeWarning(false)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('step')
          return next
        },
        { replace: true },
      )
    },
  })

  const grouped = useMemo(() => {
    if (!rows) return { matched: [], unmatched: [] }
    const matched: StatementRow[] = []
    const unmatched: StatementRow[] = []
    for (const r of rows) {
      if (r.classification === classificationEnum.MATCHED) {
        matched.push(r)
      } else {
        unmatched.push(r)
      }
    }
    return { matched, unmatched }
  }, [rows])

  const hasLineItems = rows != null && rows.length > 0 && rows.some((r) => r.classification != null)

  const _setRowFields = useCallback((rowId: string, fields: Record<string, unknown>) => {
    setEditedFields((prev) => ({ ...prev, [rowId]: fields }))
  }, [])

  const pendingMatched = useMemo(
    () => grouped.matched.filter((r) => r.decision === decisionEnum.PENDING),
    [grouped.matched],
  )

  // Whether any still-unresolved unmatched row carries LLM autofill. Gates the
  // "create expenses from AI suggestions" action on the Unmatched step.
  const hasAiSuggestions = useMemo(
    () =>
      grouped.unmatched.some(
        (r) =>
          r.decision === decisionEnum.PENDING &&
          !r.matchedExpense &&
          r.autofilledFields != null &&
          Object.keys(r.autofilledFields).length > 0,
      ),
    [grouped.unmatched],
  )

  // Finish-step outcome buckets. An unmatched row counts as resolved once it is
  // linked to (or created as) an expense; the leftover pending ones are the
  // "still unmatched" warning count. Rejected rows are bucketed on their own.
  const finishCounts = useMemo(() => {
    const counts = { matchedApproved: 0, rejected: 0, resolvedFromUnmatched: 0, stillUnmatched: 0 }
    if (!rows) return counts
    for (const r of rows) {
      if (r.decision === decisionEnum.REJECTED) {
        counts.rejected++
      } else if (r.classification === classificationEnum.MATCHED) {
        if (r.decision === decisionEnum.APPROVED) counts.matchedApproved++
      } else if (r.matchedExpense) {
        counts.resolvedFromUnmatched++
      } else {
        counts.stillUnmatched++
      }
    }
    return counts
  }, [rows])

  // Unmatched-step filter chips: All · Unresolved · Resolved.
  const [unmatchedFilter, setUnmatchedFilter] = useState<'all' | 'unresolved' | 'resolved'>('all')
  const visibleUnmatched = useMemo(() => {
    if (unmatchedFilter === 'all') return grouped.unmatched
    return grouped.unmatched.filter((r) => {
      const resolved = r.matchedExpense != null || r.decision !== decisionEnum.PENDING
      return unmatchedFilter === 'resolved' ? resolved : !resolved
    })
  }, [grouped.unmatched, unmatchedFilter])

  const toggleSelectMatched = useCallback((rowId: string, selected: boolean) => {
    setSelectedMatched((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(rowId)
      } else {
        next.delete(rowId)
      }
      return next
    })
  }, [])

  const toggleSelectAllMatched = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedMatched(new Set(pendingMatched.map((r) => r.id)))
      } else {
        setSelectedMatched(new Set())
      }
    },
    [pendingMatched],
  )

  const toggleSelectUnmatched = useCallback((rowId: string, selected: boolean) => {
    setSelectedUnmatched((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(rowId)
      } else {
        next.delete(rowId)
      }
      return next
    })
  }, [])

  // Approve row mutation
  const approveRowMutation = useMutation({
    mutationFn: async (rowId: string) => {
      return statementApi.csc.approveRow({ statementId: id!, rowId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Reject row mutation
  const rejectRowMutation = useMutation({
    mutationFn: async (rowId: string) => {
      return statementApi.csc.rejectRow({ statementId: id!, rowId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Bulk approve matched rows
  const bulkApproveMutation = useMutation({
    mutationFn: async (rowIds: string[]) => {
      return statementApi.csc.bulkApprove({ statementId: id!, rowIds })
    },
    onSuccess: () => {
      setSelectedMatched(new Set())
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Bulk reject matched rows
  const bulkRejectMutation = useMutation({
    mutationFn: async (rowIds: string[]) => {
      return statementApi.csc.bulkReject({ statementId: id!, rowIds })
    },
    onSuccess: () => {
      setSelectedMatched(new Set())
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Finalize review mutation (in_review → confirmed)
  const finalizeReviewMutation = useMutation({
    mutationFn: async () => {
      return statementApi.csc.confirmReview(id!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
    },
  })

  // Reopen review mutation (confirmed → in_review)
  const reopenReviewMutation = useMutation({
    mutationFn: async () => {
      return statementApi.csc.reopenReview(id!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
    },
  })

  // Override the auto-detected report for this statement's new expenses.
  const setReportMutation = useMutation({
    mutationFn: async (reportId: string | null) => {
      return statementApi.csc.setReport({ statementId: id!, reportId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
    },
  })

  // Confirm All mutation
  const confirmAllMutation = useMutation({
    mutationFn: async () => {
      const rowActions = rows?.map((row) => {
        const fields = editedFields[row.id]
        return {
          id: row.id,
          action: (row.decision === decisionEnum.REJECTED ? 'reject' : 'approve') as
            | 'approve'
            | 'reject',
          ...(fields ? { autofilledFields: fields } : {}),
        }
      })
      return statementApi.csc.confirm({
        statementId: id!,
        rows: rowActions,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Unlink row from expense
  const unlinkRowMutation = useMutation({
    mutationFn: async (rowId: string) => {
      return statementApi.csc.unlinkRow({ statementId: id!, rowId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Link row to existing expense
  const linkRowMutation = useMutation({
    mutationFn: async ({ rowId, expenseId }: { rowId: string; expenseId: string }) => {
      return statementApi.csc.linkRow({
        statementId: id!,
        rowId,
        expenseId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      refetchRows()
    },
  })

  // Download reconciled CSV
  const [isDownloading, setIsDownloading] = useState(false)
  const handleDownloadCsv = useCallback(async () => {
    if (!id) return
    setIsDownloading(true)
    try {
      const blob = await exportCsv(id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `statement_${id}_reconciled.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } finally {
      setIsDownloading(false)
    }
  }, [id])

  // Download processed (normalized) CSV
  const [isDownloadingProcessed, setIsDownloadingProcessed] = useState(false)
  const handleDownloadProcessedCsv = useCallback(() => {
    if (!statement?.normalizedCsv) return
    setIsDownloadingProcessed(true)
    try {
      const a = document.createElement('a')
      a.href = statement.normalizedCsv
      a.download = ''
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } finally {
      setIsDownloadingProcessed(false)
    }
  }, [statement?.normalizedCsv])

  // Re-reconcile mutation
  const reconcileMutation = useMutation({
    mutationFn: async () => {
      return statementApi.csc.reconcile(id!)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: statementQueries.all() })
      // Light up the reconciliation spinner immediately.
      if (data.asyncJobId) {
        restoreActive(
          buildOptimisticJob({
            id: data.asyncJobId,
            taskType: taskTypeEnum.STATEMENT_RECONCILIATION,
            entityId: id!,
            entityType: 'Statement',
            label: t('asyncJobs.statementReconciliation'),
          }),
        )
      }
    },
  })

  // Delete
  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: statementApi.remove,
    queryKey: statementQueries.all(),
    onSuccess: () => navigate('/statements'),
  })

  if (statementLoading || rowsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!statement || !rows) {
    return <div className="p-4 text-text-placeholder">{t('statementReview.notFound')}</div>
  }

  const isInReview = statement.status === statementStatusEnum.IN_REVIEW
  const isConfirmed = statement.status === statementStatusEnum.CONFIRMED
  const isReady = statement.status === statementStatusEnum.READY
  const isReconciling_ = statement.status === statementStatusEnum.RECONCILING
  // The `isProcessing`/`isReconciling` job flags both drop to false for a beat
  // during the processing→reconciliation handoff (the reconciliation job is
  // enqueued but not yet reflected in the active-jobs list). The statement's own
  // status is the continuous source of truth, so treat the whole pre-review
  // pipeline as busy to bridge that gap and avoid a loader flicker.
  const isPipelineBusy =
    statement.status === statementStatusEnum.PROCESSING || isReady || isReconciling_
  const isReadOnly = isConfirmed
  const showProcessedCsvButton =
    (isReady || isReconciling_ || isInReview || isConfirmed) && Boolean(statement.normalizedCsv)
  const isConfirming = confirmAllMutation.isPending

  // Wizard step is derived from status + rows, never stored. The URL `step`
  // param lets the user revisit earlier steps and the browser back button walk them.
  const wizard = resolveWizardState(statement, rows, requestedStep)
  const currentIndex = wizard.steps.indexOf(wizard.currentStep)
  const maxNavigableIndex = wizard.steps.indexOf(wizard.derivedStep)
  const goBack = () => {
    if (wizard.canGoBack) goToStep(wizard.steps[currentIndex - 1])
  }
  const goNext = () => {
    if (wizard.canGoNext) goToStep(wizard.steps[currentIndex + 1])
  }
  const isReviewStep =
    wizard.currentStep === wizardStepEnum.MATCHED || wizard.currentStep === wizardStepEnum.UNMATCHED

  // Each step exposes exactly one primary CTA. On terminal Finish it completes
  // (or reopens) the statement; on every other step it advances the wizard.
  type StepCta = {
    label: string
    onClick: () => void
    testid: string
    isLoading?: boolean
    disabled?: boolean
  }
  let stepCta: StepCta
  if (wizard.currentStep === wizardStepEnum.FINISH) {
    stepCta = isConfirmed
      ? {
          label: t('statementReview.reopenStatement'),
          onClick: () => reopenReviewMutation.mutate(),
          isLoading: reopenReviewMutation.isPending,
          testid: 'reopen-review-button',
        }
      : {
          label: t('statementReview.completeStatement'),
          onClick: () => finalizeReviewMutation.mutate(),
          isLoading: finalizeReviewMutation.isPending,
          testid: 'finalize-review-button',
        }
  } else if (wizard.currentStep === wizardStepEnum.ACCOUNT && !isReadOnly) {
    const accountChanged =
      Boolean(statement.paymentAccount) && selectedAccountId !== statement.paymentAccount
    stepCta = {
      label: t('statementReview.confirmAccount'),
      onClick: () => {
        if (!selectedAccountId) return
        // Changing the account after reconciliation re-runs the matches: warn first.
        if (isInReview && accountChanged) {
          setShowAccountChangeWarning(true)
        } else if (statement.paymentAccountConfirmed && !accountChanged) {
          goNext()
        } else {
          setAccountMutation.mutate(selectedAccountId)
        }
      },
      disabled: !selectedAccountId,
      isLoading: setAccountMutation.isPending,
      testid: 'confirm-account-button',
    }
  } else if (wizard.currentStep === wizardStepEnum.COLUMNS && !isReadOnly) {
    stepCta = {
      label: t('statementReview.confirmColumns'),
      onClick: () => confirmColumnsMutation.mutate(),
      disabled: !columnsValidation.valid,
      isLoading: confirmColumnsMutation.isPending,
      testid: 'confirm-columns-button',
    }
  } else {
    stepCta = {
      label: t('common.next'),
      onClick: goNext,
      disabled: !wizard.canGoNext,
      testid: 'wizard-next-button',
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{t('statementReview.title')}</h1>
          <p className="mt-1 text-sm text-text-placeholder">
            {statement.paymentAccountRef?.name ?? t('statementReview.accountFallback')} &middot;{' '}
            {t('statementsPage.rowCount', { count: statement.rowCount })} &middot;{' '}
            <span data-testid="statement-billing-org">
              {statement.billingOrganization
                ? t('statementReview.billedTo', {
                    org: statement.billingOrganizationName ?? '',
                  })
                : t('statementReview.aiOff')}
            </span>{' '}
            &middot;{' '}
            <span
              className={cn(
                'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                statementStatusBadgeClasses[statement.status] || 'bg-gray-100 text-gray-700',
              )}
            >
              {statementStatusLabelKeys[statement.status]
                ? t(statementStatusLabelKeys[statement.status])
                : statement.status}
            </span>
          </p>
        </div>
        {/* Header holds only non-primary, always-available utilities (download,
            delete). Every step's single primary CTA lives in the wizard nav row
            below, and the AI-only actions live on their specific steps. */}
        <div className="flex items-center gap-2">
          {showProcessedCsvButton && (
            <Button
              variant="secondary"
              onClick={handleDownloadProcessedCsv}
              disabled={isDownloadingProcessed}
              data-testid="download-processed-csv"
            >
              {isDownloadingProcessed ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <ArrowDownTrayIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('statementReview.downloadCsv')}
                </>
              )}
            </Button>
          )}
          {hasLineItems && (
            <Button
              variant="secondary"
              onClick={handleDownloadCsv}
              disabled={isDownloading}
              data-testid="generate-reconciled-csv"
            >
              {isDownloading ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <ArrowDownTrayIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('statementReview.generateCsv')}
                </>
              )}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() =>
              requestDelete(
                id!,
                statement.paymentAccountRef?.name ?? t('statementReview.statementFallback'),
              )
            }
            disabled={isProcessing || isReconciling}
            data-testid="delete-statement-button"
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Wizard step indicator + navigation */}
      <div className="flex items-center justify-between gap-4 border-y border-border py-3">
        <StatementStepIndicator
          steps={wizard.steps}
          currentStep={wizard.currentStep}
          maxNavigableIndex={maxNavigableIndex}
          onStepClick={goToStep}
        />
        <div className="flex shrink-0 items-center gap-2">
          {wizard.canGoBack && (
            <Button variant="ghost" onClick={goBack} data-testid="wizard-back-button">
              <ChevronLeftIcon className="mr-1 h-4 w-4" aria-hidden="true" />
              {t('common.back')}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={stepCta.onClick}
            disabled={stepCta.disabled || stepCta.isLoading}
            data-testid={stepCta.testid}
          >
            {stepCta.isLoading ? <Spinner size="xs" /> : stepCta.label}
          </Button>
        </div>
      </div>

      {/* Upload step: processing progress or a post-processing summary */}
      {wizard.currentStep === wizardStepEnum.UPLOAD && (
        <div data-testid="upload-step">
          {isProcessing && (
            <JobProgressBar taskType={taskTypeEnum.STATEMENT_PROCESSING} entityId={id!} />
          )}
          {isReconciling && (
            <JobProgressBar taskType={taskTypeEnum.STATEMENT_RECONCILIATION} entityId={id!} />
          )}
          {!isProcessing && !isReconciling && isPipelineBusy && (
            <div
              className="flex items-center gap-2 py-4 text-sm text-text-secondary"
              data-testid="statement-pipeline-loading"
            >
              <Spinner size="xs" />
              <span>{t('statementReview.preparingReview')}</span>
            </div>
          )}
          {!isProcessing && !isReconciling && !isPipelineBusy && (
            <p className="py-4 text-sm text-text-secondary">
              {statement.status === statementStatusEnum.FAILED
                ? statement.errorMessage
                : t('statementReview.progressRows', {
                    current: statement.rowCount,
                    total: statement.rowCount,
                  })}
            </p>
          )}
        </div>
      )}

      {/* Columns step: confirm or correct the AI-applied header map */}
      {wizard.currentStep === wizardStepEnum.ACCOUNT && (
        <AccountStep
          statement={statement}
          selectedAccountId={selectedAccountId}
          onChange={setSelectedAccountId}
          isReadOnly={isReadOnly}
        />
      )}

      <Modal
        isOpen={showAccountChangeWarning}
        onClose={() => setShowAccountChangeWarning(false)}
        title={t('statementReview.accountChangeTitle')}
        size="sm"
      >
        <p className="text-sm text-text-secondary" data-testid="account-change-warning">
          {t('statementReview.accountChangeWarning')}
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setShowAccountChangeWarning(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => selectedAccountId && setAccountMutation.mutate(selectedAccountId)}
            disabled={setAccountMutation.isPending}
            data-testid="account-change-confirm"
          >
            {setAccountMutation.isPending ? (
              <Spinner size="xs" />
            ) : (
              t('statementReview.confirmAccount')
            )}
          </Button>
        </div>
      </Modal>

      {wizard.currentStep === wizardStepEnum.COLUMNS && (
        <ColumnsStep
          columns={columnPreview?.columns ?? []}
          value={columnsValue}
          onChange={(rawHeader, choice) =>
            setColumnsValue((prev) => ({ ...prev, [rawHeader]: choice as ColumnsValue[string] }))
          }
          validation={columnsValidation}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Finish step: outcome summary. Pre-completion (in_review) it shows the
          counts, the report override, and completes via the wizard CTA; once
          confirmed it is the same summary read-only with the reconciled CSV
          download (Reopen lives in the wizard CTA row). */}
      {wizard.currentStep === wizardStepEnum.FINISH && (
        <div
          className={cn(
            'space-y-4 rounded-md border p-4',
            isConfirmed ? 'border-green-200 bg-green-50' : 'border-border bg-surface',
          )}
          data-testid={isConfirmed ? 'confirmed-summary' : 'finish-summary'}
        >
          {isConfirmed && (
            <p className="text-sm font-medium text-green-800">
              {t('statementReview.completedToast')}
            </p>
          )}
          <div className="flex flex-wrap gap-6 text-sm text-text-secondary">
            <span data-testid="count-matched-approved">
              {t('statementReview.matchedApprovedCount', { count: finishCounts.matchedApproved })}
            </span>
            <span data-testid="count-rejected">
              {t('statementReview.rejectedCount', { count: finishCounts.rejected })}
            </span>
            <span data-testid="count-resolved-unmatched">
              {t('statementReview.resolvedFromUnmatchedCount', {
                count: finishCounts.resolvedFromUnmatched,
              })}
            </span>
            <span
              data-testid="count-still-unmatched"
              className={cn(finishCounts.stillUnmatched > 0 && 'font-medium text-warning')}
            >
              {t('statementReview.stillUnmatchedCount', { count: finishCounts.stillUnmatched })}
            </span>
          </div>

          <ReportOverride
            suggestedReportId={statement.suggestedReport}
            suggestedReportName={statement.suggestedReportRef?.name}
            onChange={(reportId) => setReportMutation.mutate(reportId)}
            disabled={isReadOnly}
          />

          {isConfirmed && hasLineItems && (
            <Button
              variant="secondary"
              onClick={handleDownloadCsv}
              disabled={isDownloading}
              data-testid="finish-download-reconciled-csv"
            >
              {isDownloading ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <ArrowDownTrayIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('statementReview.generateCsv')}
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {isReviewStep && confirmAllMutation.isSuccess && (
        <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
          {t('statementReview.batchConfirmedDetail', {
            reconciled: confirmAllMutation.data.reconciled.length,
            created: confirmAllMutation.data.created.length,
            skipped: confirmAllMutation.data.skipped,
          })}
        </div>
      )}

      {/* Re-reconcile is an AI-only helper on the Matched step (with a fallback on
          the Upload step for the pre-review READY edge case); absent when the org
          flag is off or a job is already running. */}
      {aiReconciliationEnabled &&
        !isProcessing &&
        !isReconciling &&
        ((wizard.currentStep === wizardStepEnum.MATCHED && isInReview) ||
          (wizard.currentStep === wizardStepEnum.UPLOAD && isReady)) && (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => reconcileMutation.mutate()}
              disabled={reconcileMutation.isPending}
              data-testid="re-reconcile-button"
            >
              {reconcileMutation.isPending ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <ArrowPathIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t('statementReview.reReconcile')}
                </>
              )}
            </Button>
          </div>
        )}

      {/* "Create expenses from AI suggestions" (the former Confirm All) is a
          secondary AI-only action on the Unmatched step. */}
      {wizard.currentStep === wizardStepEnum.UNMATCHED &&
        aiReconciliationEnabled &&
        isInReview &&
        hasAiSuggestions && (
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => confirmAllMutation.mutate()}
              disabled={isConfirming}
              data-testid="confirm-all-button"
            >
              {isConfirming ? <Spinner size="xs" /> : t('statementReview.confirmAllAi')}
            </Button>
          </div>
        )}

      {/* Matched + Unmatched review board */}
      {isReviewStep && (
        <>
          <Section
            title={t('statementReview.matchedSection')}
            count={grouped.matched.length}
            color="bg-green-100 text-green-800"
            selectAll={
              isInReview && pendingMatched.length > 0
                ? {
                    checked:
                      pendingMatched.length > 0 &&
                      pendingMatched.every((r) => selectedMatched.has(r.id)),
                    indeterminate:
                      selectedMatched.size > 0 &&
                      !pendingMatched.every((r) => selectedMatched.has(r.id)),
                    onChange: toggleSelectAllMatched,
                  }
                : undefined
            }
          >
            {grouped.matched.length === 0 ? (
              <EmptyState>{t('statementReview.noMatched')}</EmptyState>
            ) : (
              grouped.matched.map((row) => (
                <MatchedRowCard
                  key={row.id}
                  row={row}
                  onApprove={() => approveRowMutation.mutate(row.id)}
                  onReject={() => rejectRowMutation.mutate(row.id)}
                  onUnlink={() => unlinkRowMutation.mutate(row.id)}
                  onLinkExpense={(expenseId) =>
                    linkRowMutation.mutate({ rowId: row.id, expenseId })
                  }
                  isReadOnly={isReadOnly}
                  isLinking={linkRowMutation.isPending}
                  isApproving={approveRowMutation.isPending}
                  isRejecting={rejectRowMutation.isPending}
                  isUnlinking={unlinkRowMutation.isPending}
                  selected={selectedMatched.has(row.id)}
                  onSelectChange={isInReview ? (s) => toggleSelectMatched(row.id, s) : undefined}
                />
              ))
            )}
          </Section>

          {/* Unmatched Section */}
          <Section
            title={t('statementReview.unmatchedSection')}
            count={grouped.unmatched.length}
            color="bg-primary-100 text-primary-800"
          >
            {wizard.currentStep === wizardStepEnum.UNMATCHED && (
              <div className="mb-3 flex gap-2" data-testid="unmatched-filter-chips">
                {(['all', 'unresolved', 'resolved'] as const).map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setUnmatchedFilter(chip)}
                    aria-pressed={unmatchedFilter === chip}
                    data-testid={`unmatched-filter-${chip}`}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      unmatchedFilter === chip
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-text-secondary hover:bg-gray-200',
                    )}
                  >
                    {t(`statementReview.filter_${chip}`)}
                  </button>
                ))}
              </div>
            )}
            {visibleUnmatched.length === 0 ? (
              <EmptyState>{t('statementReview.noUnmatched')}</EmptyState>
            ) : (
              visibleUnmatched.map((row) => (
                <UnmatchedRowCard
                  key={row.id}
                  row={row}
                  onLinkExpense={(expenseId) =>
                    linkRowMutation.mutate({ rowId: row.id, expenseId })
                  }
                  onUnlink={() => unlinkRowMutation.mutate(row.id)}
                  isReconciled={isReadOnly}
                  isLinking={linkRowMutation.isPending}
                  isUnlinking={unlinkRowMutation.isPending}
                  paymentAccountName={statement?.paymentAccountRef?.name}
                  paymentAccountId={statement?.paymentAccount ?? undefined}
                  selected={selectedUnmatched.has(row.id)}
                  onSelectChange={(selected) => toggleSelectUnmatched(row.id, selected)}
                />
              ))
            )}
          </Section>
        </>
      )}

      {/* Floating bulk action bar */}
      {isReviewStep && selectedMatched.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 z-sticky flex -translate-x-1/2 items-center gap-4 rounded-lg border border-border bg-surface px-6 py-3 shadow-lg"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium text-gray-700" data-testid="bulk-selection-count">
            {t('statementReview.selectedCount', { count: selectedMatched.size })}
          </span>
          <Button
            variant="primary"
            onClick={() => bulkApproveMutation.mutate(Array.from(selectedMatched))}
            disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
            data-testid="bulk-approve-button"
          >
            {bulkApproveMutation.isPending ? (
              <Spinner size="xs" />
            ) : (
              <>
                <CheckIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                {t('statementReview.approveSelected')}
              </>
            )}
          </Button>
          <Button
            variant="danger"
            onClick={() => bulkRejectMutation.mutate(Array.from(selectedMatched))}
            disabled={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
            data-testid="bulk-reject-button"
          >
            {bulkRejectMutation.isPending ? (
              <Spinner size="xs" />
            ) : (
              <>
                <XMarkIcon className="mr-1 h-4 w-4" aria-hidden="true" />
                {t('statementReview.rejectSelected')}
              </>
            )}
          </Button>
        </div>
      )}

      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  )
}

function Section({
  title,
  count,
  color,
  children,
  selectAll,
}: {
  title: string
  count: number
  color: string
  children: React.ReactNode
  selectAll?: {
    checked: boolean
    indeterminate: boolean
    onChange: (checked: boolean) => void
  }
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        {selectAll && (
          <input
            type="checkbox"
            checked={selectAll.checked}
            ref={(el) => {
              if (el) el.indeterminate = selectAll.indeterminate
            }}
            onChange={(e) => selectAll.onChange(e.target.checked)}
            className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
            data-testid="select-all-matched"
          />
        )}
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        <span
          className={cn('rounded-full px-2 py-0.5 text-xs font-medium', color)}
          data-testid={`section-count-${title.toLowerCase()}`}
        >
          {count}
        </span>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <p className="py-4 text-center text-sm text-text-tertiary">{children}</p>
}

export const StatementReviewWrapper = () => {
  return <StatementReview />
}
